import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {BreakpointObserver} from '@angular/cdk/layout';
import {GlobalSearchService} from '../../services/search/global-search.service';
import {GlobalSearchResultGroup} from '../../dtos/search/global-search-results';
import {SEARCH_TYPE_LABELS} from '../../shared/search/search-type-labels';
import {SearchResultType} from '../../shared/search/search-result-type';
import {navigateToSearchResult} from '../../shared/search/search-navigation';
import {NotificationService} from '../../services/common/notification.service';

const ROWS_PER_DOMAIN = 3;

const COLS_BY_BREAKPOINT: { query: string, cols: number }[] = [
  {query: '(max-width: 599px)', cols: 1},
  {query: '(min-width: 600px) and (max-width: 959px)', cols: 2},
  {query: '(min-width: 960px) and (max-width: 1279px)', cols: 3},
  {query: '(min-width: 1280px)', cols: 4}
];

@Component({
  selector: 'app-search-results',
  templateUrl: './search-results.component.html',
  styleUrl: './search-results.component.scss',
  standalone: false
})
export class SearchResultsComponent implements OnInit, OnDestroy {
  query = '';
  groups: GlobalSearchResultGroup[] = [];
  isLoading = false;
  cols = COLS_BY_BREAKPOINT[COLS_BY_BREAKPOINT.length - 1].cols;

  readonly typeLabels = SEARCH_TYPE_LABELS;

  private breakpointSubscription: Subscription;

  constructor(private route: ActivatedRoute, private router: Router,
              private globalSearchService: GlobalSearchService,
              private notificationService: NotificationService,
              private breakpointObserver: BreakpointObserver) {
  }

  ngOnInit() {
    this.breakpointSubscription = this.breakpointObserver
      .observe(COLS_BY_BREAKPOINT.map(entry => entry.query))
      .subscribe(state => {
        const matched = COLS_BY_BREAKPOINT.find(entry => state.breakpoints[entry.query]);
        const newCols = matched?.cols ?? this.cols;
        if (newCols !== this.cols) {
          this.cols = newCols;
          if (this.query) {
            this.runSearch();
          }
        }
      });

    this.route.queryParams.subscribe(params => {
      this.query = params['q'] ?? '';
      if (this.query) {
        this.runSearch();
      } else {
        this.groups = [];
      }
    });
  }

  ngOnDestroy() {
    this.breakpointSubscription?.unsubscribe();
  }

  private runSearch() {
    this.isLoading = true;
    this.globalSearchService.search(this.query, this.cols * ROWS_PER_DOMAIN).subscribe({
      next: result => {
        this.isLoading = false;
        this.groups = result.groups;
      },
      error: () => {
        this.isLoading = false;
        this.groups = [];
      }
    });
  }

  openResult(typeOf: string, id: number) {
    if (!navigateToSearchResult(this.router, typeOf, id)) {
      this.notificationService.openSnackBar('Derzeit wird diese Funktion für diese Art nicht unterstützt');
    }
  }

  showAllInGroup(group: GlobalSearchResultGroup) {
    if (this.isCategoryType(group.type)) {
      this.globalSearchService.searchType(this.query, group.type, group.totalMatches).subscribe({
        next: fullGroup => {
          group.results = fullGroup.results;
        },
        error: () => {
          this.notificationService.openSnackBar('Die restlichen Themen konnten nicht geladen werden');
        }
      });
    } else {
      this.viewAllInDomain(group.type);
    }
  }

  isCategoryType(type: SearchResultType): boolean {
    return type.endsWith('_CATEGORY');
  }

  viewAllInDomain(type: SearchResultType) {
    this.router.navigate([this.typeLabels[type].route], {queryParams: {q: this.query}});
  }
}
